/**
 * Types for a submission's **visible** data — the list a host renders as key/value
 * pairs (one row per filled, visible form element).
 *
 * The webview emits this as `visible_submissions_data` inside the `submission_body`
 * payload: an array of {@link VisibleSubmissionDataItem}, discriminated by
 * `element_type`, where `element_value`'s shape depends on the type. Narrow on
 * `element_type` to get a precisely-typed `element_value` and render it however you
 * like:
 *
 * @example
 * for (const item of submission.visible_submissions_data ?? []) {
 *   switch (item.element_type) {
 *     case FormElementType.email:
 *       row(item.element_title, item.element_value); // element_value: string
 *       break;
 *     case FormElementType.checkbox:
 *       // element_value: string[] | OrderCheckboxSubmissionData
 *       break;
 *     // …
 *   }
 * }
 */

/* ------------------------------------------------------------------ *
 * Element types
 * ------------------------------------------------------------------ */

/**
 * Every form element type, mirroring the webview's `FormElementType`. The string
 * values are the wire values. Structural types (sections, separators, …) never carry
 * a value and never appear in visible submission data.
 */
export enum FormElementType {
    email = "email",
    section = "section_block",
    html_block = "section",
    url = "url",
    text = "text",
    select = "select",
    radio = "radio",
    simple_name = "simple_name",
    textarea = "textarea",
    time = "time",
    address = "address",
    money = "money",
    number = "number",
    date = "date",
    phone = "phone",
    simple_phone = "simple_phone",
    checkbox = "checkbox",
    image = "image",
    business_card = "business_card",
    signature = "signature",
    barcode = "barcode",
    separator = "column_separator",
    activation = "activation",
    document = "documents",
    datetime = "datetime",
    meeting = "meeting",
    audio = "audio",
    rating = "rating",
    assign_owner = "assign_owner",
    boolean = "boolean",
    engagem_feeder = "engagem_feeder",
    speaker_section = "speaker_section_block",
    session_section = "session_section_block",
    image_placeholder = "image_placeholder",
    star_rating = "star_survey",
    attachments = "attachments",
}

/* ------------------------------------------------------------------ *
 * Element value shapes
 * ------------------------------------------------------------------ */

/**
 * `simple_name` value: the visible sub-fields keyed by their transcription identifier
 * (`ll_field_unique_identifier`), the same identifiers used by prefill. Hidden sub-fields are
 * omitted, so keys are optional.
 */
export type NameSubmissionValue = {
    FirstName?: string;
    LastName?: string;
};

/**
 * `address` value: the visible sub-fields keyed by their transcription identifier
 * (`ll_field_unique_identifier`). Hidden sub-fields are omitted, so keys are optional.
 */
export type AddressSubmissionValue = {
    StreetAddress?: string;
    StreetAddress2?: string;
    City?: string;
    State?: string;
    Zipcode?: string;
    Country?: string;
};

/** Checkbox value when the element is an "order" checkbox (quantities + note). */
export type OrderCheckboxSubmissionData = {
    values: { value: string; quantity: number }[];
    note: string;
};

/** Radio/select value when the element is an "order" radio/select (quantity + note). */
export type OrderRadioSubmissionData = { value: string; quantity: number; note: string };

/** An uploaded attachment. `blob` is not present on the wire (postMessage JSON drops it). */
export type AttachmentValue = { token: string; url: string; name: string; size: number };

/** A business-card capture: front/back image references (either may be absent). */
export type BusinessCardValue = { front: string; back: string };

/** One engagement-feeder question/answer entry. */
export type SubmissionQuestionData = {
    question: string;
    answers: string[];
    /** `""` when the element is hidden. */
    correct_answer: number | "";
};

/**
 * Maps each value-carrying element type to its `element_value` shape. Structural
 * types are intentionally absent, so they can never appear in a visible item.
 */
export type VisibleSubmissionElementValueMap = {
    [FormElementType.email]: string;
    [FormElementType.url]: string;
    [FormElementType.text]: string;
    [FormElementType.textarea]: string;
    [FormElementType.money]: string;
    [FormElementType.number]: string;
    [FormElementType.phone]: string;
    [FormElementType.simple_phone]: string;
    [FormElementType.date]: string;
    [FormElementType.time]: string;
    [FormElementType.datetime]: string;
    [FormElementType.audio]: string;
    [FormElementType.rating]: string;
    [FormElementType.star_rating]: string;
    [FormElementType.assign_owner]: string;
    [FormElementType.signature]: string;
    [FormElementType.barcode]: string;
    [FormElementType.meeting]: string;
    [FormElementType.activation]: string;
    [FormElementType.checkbox]: string[] | OrderCheckboxSubmissionData;
    [FormElementType.radio]: string | OrderRadioSubmissionData;
    [FormElementType.select]: string | OrderRadioSubmissionData;
    [FormElementType.image]: string[];
    [FormElementType.document]: number[];
    [FormElementType.engagem_feeder]: SubmissionQuestionData[];
    [FormElementType.business_card]: Partial<BusinessCardValue>;
    [FormElementType.boolean]: boolean;
    [FormElementType.attachments]: AttachmentValue[];
    [FormElementType.simple_name]: NameSubmissionValue;
    [FormElementType.address]: AddressSubmissionValue;
};

/** Element types that carry a user-facing value (i.e. can appear in visible data). */
export type VisibleSubmissionElementType = keyof VisibleSubmissionElementValueMap;

/**
 * One entry in `visible_submissions_data`, discriminated by `element_type`. Narrowing
 * on `element_type` gives a precisely-typed `element_value`.
 */
export type VisibleSubmissionDataItem = {
    [T in VisibleSubmissionElementType]: {
        element_id: string;
        element_title: string;
        element_type: T;
        element_value: VisibleSubmissionElementValueMap[T];
    };
}[VisibleSubmissionElementType];
